import { useRef, useState } from 'react'
import { Trash2 } from 'lucide-react'
import { Button, Checkbox, Chip, Input, Textarea } from '@/components/ui'
import { cn } from '@/lib/utils/cn'
import type { LineAction, PriceReviewLine, ReviewLinePatch } from '../api/reviewTypes'
import { formatMoney, formatPct, pctChange } from '../utils/letter-lines'
import {
  ROW_TONE_CLASS,
  currentMarginPct,
  lastOrderLabel,
  marginAtScheduledCost,
  newMarginPct,
  priceForMargin,
  rollingLabel,
  rowTone,
  scheduledCostChangePct,
  tariffAmount,
  totalPrice,
  unitsDiffer,
} from '../utils/review-lines'
import { formatShortDate } from '../utils/upcoming-cost'
import { NumberCell } from './LetterLinesGrid'

/** Decision line: flag, product, our cost, old, MARGIN NOW, NEW, change, total, new margin, action (+ remove). */
const COLS = 'grid-cols-[40px_minmax(168px,1fr)_150px_100px_112px_130px_80px_110px_120px_120px]'
const COLS_WITH_REMOVE = 'grid-cols-[40px_minmax(168px,1fr)_150px_100px_112px_130px_80px_110px_120px_120px_44px]'

/** Multi-line text saved on blur. Seeded once, so a background refresh never overwrites typing. */
function NoteBox({ value, label, disabled, onCommit }: { value: string | null; label: string; disabled: boolean; onCommit: (v: string | null) => void }) {
  const [draft, setDraft] = useState(value ?? '')
  return (
    <Textarea
      aria-label={label}
      rows={1}
      value={draft}
      disabled={disabled}
      placeholder="Internal note, never printed on the letter"
      className="min-h-[var(--input-height)] max-h-40 resize-y py-[7px] [field-sizing:content]"
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => {
        const next = draft.trim() === '' ? null : draft.trim()
        if (next !== (value ?? null)) onCommit(next)
      }}
    />
  )
}

/** Single-line text saved on blur (customer part # on a line you added). */
function TextField({ value, label, disabled, placeholder, onCommit }: { value: string | null; label: string; disabled: boolean; placeholder: string; onCommit: (v: string | null) => void }) {
  const [draft, setDraft] = useState(value ?? '')
  return (
    <Input
      aria-label={label}
      value={draft}
      disabled={disabled}
      placeholder={placeholder}
      className="w-56"
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => {
        const next = draft.trim() === '' ? null : draft.trim()
        if (next !== (value ?? null)) onCommit(next)
      }}
    />
  )
}

/** A labelled value on one line in the context strip. */
function Inline({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <span className="flex h-[var(--input-height)] items-center gap-2 whitespace-nowrap text-md text-primary">
      <span className="text-sm font-medium text-tertiary">{label}</span>
      <span>{children}</span>
    </span>
  )
}

/**
 * Margin % box. Shows the margin the current New price gives; typing a margin
 * sets the price (cost / (1 - margin), rounded up to the cent) through `onCommit`.
 */
function MarginBox({ value, label, onCommit }: { value: number | null; label: string; onCommit: (marginPct: number | null) => void }) {
  const shown = value === null ? '' : value.toFixed(1)
  const [draft, setDraft] = useState(shown)
  return (
    <div className="relative w-full">
      <Input
        aria-label={label}
        inputMode="decimal"
        value={draft}
        placeholder="Margin"
        data-nav="margin"
        className="pr-7 text-right text-lg font-semibold tabular-nums"
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => {
          const text = draft.trim().replace(/%$/, '').trim()
          if (text === '' || text === shown) return setDraft(shown)
          const m = Number(text)
          if (Number.isNaN(m)) {
            setDraft(shown)
            return onCommit(null) // not a number: the caller says so
          }
          onCommit(m)
        }}
      />
      <span aria-hidden="true" className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-md text-tertiary">%</span>
    </div>
  )
}

const pctText = (v: number) => `${v.toFixed(1)}%`

/**
 * One line that tells the whole story of a scheduled vendor cost change: who,
 * when, what the cost does, and what it does to the margin at today's price and
 * at the new price. Shown only on products that have one.
 */
function ScheduledCostNote({ line, symbol }: { line: PriceReviewLine; symbol: string }) {
  if (line.upcoming_cost_price === null) return null
  const when = line.upcoming_cost_effective_date ? formatShortDate(line.upcoming_cost_effective_date) : 'a set date'
  const pct = scheduledCostChangePct(line)
  const unit = line.upcoming_cost_uom_name ? ` / ${line.upcoming_cost_uom_name}` : ''
  const nowOld = currentMarginPct(line)
  const afterOld = marginAtScheduledCost(line.old_price, line)
  const nowNew = newMarginPct(line)
  const afterNew = marginAtScheduledCost(line.new_price, line)
  const m = (from: number | null, to: number | null) =>
    from !== null && to !== null ? (
      <>
        {pctText(from)} → <strong className={cn('font-semibold', to < 0 && 'text-danger-text')}>{pctText(to)}</strong>
      </>
    ) : null
  const costPart =
    pct !== null && line.cost_price !== null
      ? `cost ${formatMoney(symbol, line.cost_price)} → ${formatMoney(symbol, line.upcoming_cost_price)}${unit} (${formatPct(pct)})`
      : `new cost ${formatMoney(symbol, line.upcoming_cost_price)}${unit}`
  const oldPart = m(nowOld, afterOld)
  const newPart = m(nowNew, afterNew)
  return (
    <div
      role="note"
      aria-label="Scheduled vendor cost change"
      className="mt-2 ml-[52px] rounded-sm border border-info-border bg-info-bg px-3 py-2 text-md text-info-text"
    >
      <strong className="font-semibold">Vendor cost change</strong>
      {line.upcoming_cost_vendor_name ? ` · ${line.upcoming_cost_vendor_name}` : ''} · {when}: {costPart}.
      {oldPart || newPart ? ' Margin ' : ' No margin comparison: the unit differs from the price.'}
      {oldPart ? <>at today&apos;s price {oldPart}</> : null}
      {oldPart && newPart ? '; ' : ''}
      {newPart ? <>at the new price {newPart}</> : null}
      {oldPart || newPart ? '.' : ''}
    </div>
  )
}

export interface ReviewLinesGridProps {
  lines: PriceReviewLine[]
  symbol: string
  /** Sales edits a draft; only an admin edits a submitted review. */
  editable: boolean
  /** Whether a pricelist line can be removed. Only lines added in this review can. */
  canRemove: boolean
  onPatch: (lineId: number, changes: ReviewLinePatch) => void
  onRemove: (lineId: number) => void
  /** Bump when a save is rejected: cells re-seed from the saved values instead of keeping rejected input. */
  resetKey?: number
  /** A typed margin that cannot give a price (not a number, or 100% or more). */
  onInvalidMargin?: (message: string) => void
}

/**
 * The per-customer pricing workbook, one two-line row per product so it fits the
 * screen at a readable size: the first line is the decision (old, NEW, change,
 * total, margin, action), the second is the context (cost, history, tariff, note).
 * Enter in a New price jumps to the next row's New price, like a sheet.
 */
export function ReviewLinesGrid({ lines, symbol, editable, canRemove, onPatch, onRemove, resetKey = 0, onInvalidMargin }: ReviewLinesGridProps) {
  const root = useRef<HTMLDivElement | null>(null)
  const cols = canRemove ? COLS_WITH_REMOVE : COLS

  function onKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    const el = e.target as HTMLElement
    const nav = el.getAttribute('data-nav')
    if (e.key !== 'Enter' || !nav || !root.current) return
    e.preventDefault()
    const inputs = Array.from(root.current.querySelectorAll<HTMLInputElement>(`input[data-nav="${nav}"]:not(:disabled)`))
    const at = inputs.indexOf(el as HTMLInputElement)
    inputs[at + (e.shiftKey ? -1 : 1)]?.focus()
  }

  return (
    // Below ~1540 px of window (sidebar open) the grid scrolls sideways on its own; above that it fits, and the
    // page scrolls, which keeps the header row sticky.
    <div ref={root} role="table" aria-label="Products on this review" className="max-[1540px]:overflow-x-auto" onKeyDown={onKeyDown}>
      <div className="min-w-[1270px]">
        <div
          role="row"
          className={cn(
            'sticky top-0 z-10 grid items-end gap-x-3 border-b border-border-default bg-surface-sunk px-4 py-2',
            'border-l-4 border-l-transparent whitespace-nowrap text-sm font-semibold uppercase tracking-wide text-tertiary',
            cols,
          )}
        >
          <span role="columnheader">Flag</span>
          <span role="columnheader">Product</span>
          <span role="columnheader">Our cost</span>
          <span role="columnheader" className="text-right">Old price</span>
          <span role="columnheader" className="text-right">Margin now</span>
          <span role="columnheader" className="text-right">New price</span>
          <span role="columnheader" className="text-right">Change</span>
          <span role="columnheader" className="text-right">Total</span>
          <span role="columnheader" className="text-right">New margin</span>
          <span role="columnheader">Action</span>
          {canRemove ? <span role="columnheader" /> : null}
        </div>

        {lines.map((l) => {
          const tone = rowTone(l)
          const margin = newMarginPct(l)
          const total = totalPrice(l.new_price, l.tariff_pct)
          const tariff = tariffAmount(l.new_price, l.tariff_pct)
          const name = l.product_name
          return (
            <div
              key={l.id}
              role="row"
              data-tone={tone ?? undefined}
              className={cn(
                'border-b border-border-subtle border-l-4 px-4 py-3',
                tone ? ROW_TONE_CLASS[tone] : 'border-l-transparent bg-surface',
              )}
            >
              <div className={cn('grid items-center gap-x-3', cols)}>
                <div role="cell" className="flex justify-center">
                  <Checkbox
                    aria-label={`Flag ${name} for discussion`}
                    checked={l.flagged}
                    disabled={!editable}
                    onChange={(e) => onPatch(l.id, { flagged: e.target.checked })}
                  />
                </div>

                <div role="cell" className="flex min-w-0 flex-col gap-0.5">
                  <span className="flex flex-wrap items-center gap-2 text-lg font-semibold leading-snug text-primary">
                    {name}
                    {l.source === 'added' ? <Chip variant="info">Added</Chip> : null}
                    {l.min_quantity ? <Chip variant="neutral">Min qty {l.min_quantity.toLocaleString('en-US')}</Chip> : null}
                  </span>
                  <span className="flex flex-wrap items-center gap-x-3 gap-y-1 text-md text-secondary">
                    {l.source === 'added' ? (
                      <TextField
                        key={`${l.id}-part-${l.customer_part_number}-${resetKey}`}
                        label={`Customer part number for ${name}`}
                        value={l.customer_part_number}
                        disabled={!editable}
                        placeholder="Customer part #"
                        onCommit={(v) => onPatch(l.id, { customer_part_number: v })}
                      />
                    ) : (
                      <span>{l.customer_part_number ? `Part # ${l.customer_part_number}` : 'No part #'}</span>
                    )}
                    {l.inactive ? <span className="font-medium text-danger-text">No volume in 12 months</span> : null}
                    {l.flagged ? <span className="font-medium text-warning-text">Flagged</span> : null}
                    {l.write_error ? <span className="font-medium text-danger-text">Push failed: {l.write_error}</span> : null}
                  </span>
                </div>

                <div role="cell" className="flex min-w-0 flex-col">
                  {l.cost_price === null ? (
                    <span className="text-md text-secondary">No cost on file</span>
                  ) : (
                    <>
                      <span className="text-lg tabular-nums text-primary">
                        {formatMoney(symbol, l.cost_price)}
                        {l.cost_uom_name ? <span className="text-md text-secondary"> / {l.cost_uom_name}</span> : null}
                      </span>
                      <span className="text-sm text-secondary">
                        {l.cost_source === 'standard_price' ? 'Standard cost (no PO yet)' : 'Last received PO'}
                      </span>
                    </>
                  )}
                </div>

                <div role="cell" className="text-right text-lg tabular-nums text-primary">
                  {l.old_price === null ? '—' : formatMoney(symbol, l.old_price)}
                </div>

                <div
                  role="cell"
                  className="text-right text-lg font-semibold tabular-nums text-primary"
                  title={
                    currentMarginPct(l) !== null
                      ? 'Margin on the current price at the current cost'
                      : l.cost_price === null
                        ? 'No cost on file, so no margin'
                        : unitsDiffer(l)
                          ? `Cost is per ${l.cost_uom_name ?? 'another unit'}, price is per ${l.price_uom_name ?? 'another unit'}`
                          : undefined
                  }
                >
                  {currentMarginPct(l) !== null ? `${currentMarginPct(l)!.toFixed(1)}%` : '—'}
                </div>

                <div
                  role="cell"
                  data-changed={l.price_changed || undefined}
                  className={cn('rounded-sm', l.price_changed && 'bg-success-bg ring-2 ring-success-border')}
                >
                  <NumberCell
                    key={`${l.id}-${l.new_price}-${resetKey}`}
                    label={`New price for ${name}`}
                    value={l.new_price}
                    disabled={!editable}
                    onCommit={(v) => onPatch(l.id, { new_price: v })}
                    className="text-right text-lg font-semibold tabular-nums"
                    inputProps={{ 'data-nav': 'price' }}
                  />
                </div>

                <div role="cell" className="text-right text-lg tabular-nums text-primary">
                  {formatPct(pctChange(l.old_price, l.new_price))}
                </div>

                <div role="cell" className="flex flex-col items-end">
                  <span className="text-lg tabular-nums text-primary">{total === null ? '—' : formatMoney(symbol, total)}</span>
                  {tariff !== null && tariff > 0 ? <span className="text-sm text-secondary tabular-nums">incl. {formatMoney(symbol, tariff)} tariff</span> : null}
                </div>

                <div role="cell" className="flex flex-col items-end gap-0.5 text-lg font-semibold tabular-nums text-primary">
                  {editable && l.cost_price !== null && !unitsDiffer(l) ? (
                    <MarginBox
                      key={`${l.id}-margin-${l.new_price}-${resetKey}`}
                      label={`New margin for ${name}`}
                      value={margin}
                      onCommit={(m) => {
                        const price = m === null ? null : priceForMargin(l.cost_price, m)
                        if (price === null) return onInvalidMargin?.('Type a margin below 100%.')
                        if (price !== l.new_price) onPatch(l.id, { new_price: price })
                      }}
                    />
                  ) : margin !== null ? (
                    `${margin.toFixed(1)}%`
                  ) : unitsDiffer(l) && l.new_price !== null ? (
                    <Chip variant="warning" title={`Cost is per ${l.cost_uom_name ?? 'another unit'}, price is per ${l.price_uom_name ?? 'another unit'}`}>
                      Units differ
                    </Chip>
                  ) : (
                    '—'
                  )}
                </div>

                <div role="cell">
                  <select
                    aria-label={`Action for ${name}`}
                    value={l.action}
                    disabled={!editable}
                    onChange={(e) => onPatch(l.id, { action: e.target.value as LineAction })}
                    className="h-[var(--input-height)] w-full rounded-sm border border-border-default bg-surface px-2 text-md text-primary"
                  >
                    <option value="keep">Keep</option>
                    <option value="change">Change</option>
                    <option value="discontinue">Discontinue</option>
                  </select>
                </div>

                {canRemove ? (
                  <div role="cell" className="flex justify-end">
                    {l.source === 'added' ? (
                      <Button variant="ghost" size="sm" aria-label={`Remove ${name}`} onClick={() => onRemove(l.id)}>
                        <Trash2 size={16} />
                      </Button>
                    ) : null}
                  </div>
                ) : null}
              </div>

              <div className="mt-2 grid grid-cols-[minmax(390px,1.1fr)_220px_170px_minmax(320px,2.4fr)] items-start gap-x-6 pl-[52px]">
                <Inline label="Last order">
                  {lastOrderLabel(l)}
                  {l.hist_margin_pct === null ? null : <span className="text-secondary"> · {l.hist_margin_pct.toFixed(1)}% margin</span>}
                </Inline>
                <Inline label="Last 12 months">{rollingLabel(l)}</Inline>
                <label className="flex items-center gap-2">
                  <span className="whitespace-nowrap text-sm font-medium text-tertiary">Tariff %</span>
                  <NumberCell
                    key={`${l.id}-${l.tariff_pct}-${resetKey}`}
                    label={`Tariff % for ${name}`}
                    value={l.tariff_pct}
                    disabled={!editable}
                    onCommit={(v) => onPatch(l.id, { tariff_pct: v })}
                    className="w-24 text-right tabular-nums"
                  />
                </label>
                <label className="flex min-w-0 items-start gap-2">
                  <span className="shrink-0 pt-2.5 text-sm font-medium text-tertiary">Note</span>
                  <NoteBox
                    key={`${l.id}-note-${l.note}-${resetKey}`}
                    label={`Note for ${name}`}
                    value={l.note}
                    disabled={!editable}
                    onCommit={(v) => onPatch(l.id, { note: v })}
                  />
                </label>
              </div>
              <ScheduledCostNote line={l} symbol={symbol} />
            </div>
          )
        })}
      </div>
    </div>
  )
}
